import { DateTime, DateTimeFormatOptions } from 'luxon';

import { getTenantUnits } from '@core/configuration';
import Enums from '@core/types/api/Enums';

import { ParseServerDate } from '../utils/dateHelpers';
import { hasStringValue } from './equality';

export const formatNumber = (
  value: string | number | null | undefined,
  minFraction: number = 0,
  maxFraction: number = 2,
  locale: string | null = null,
): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    value = parseFloat(value);
  }

  const useLocale = locale ?? getTenantUnits().locale;

  return Intl.NumberFormat(useLocale, {
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
  }).format(value);
};

const formatLuxonDateTime = (
  dt: DateTime | null | undefined,
  format: DateTimeFormatOptions = DateTime.DATE_MED,
  locale: string | null = null,
): string => {
  const useLocale = locale ?? getTenantUnits().locale;
  return dt && dt.isValid ? dt.setLocale(useLocale).toLocaleString(format) : '';
};

const formatStringDateTime = (
  value: string | null | undefined,
  format: DateTimeFormatOptions = DateTime.DATE_MED,
): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return formatLuxonDateTime(ParseServerDate(value), format);
};

const _currencyFormatters = new Map<string, Intl.NumberFormat>();

const getCurrencyFormatter = (
  currencyCode: string,
  locale: string,
  maximumFractionDigits: number = 2,
): Intl.NumberFormat => {
  const key = `${currencyCode}:${locale}:${maximumFractionDigits}`;
  let fmt = _currencyFormatters.get(key);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits,
      });
    } catch {
      // An invalid currency code throws a RangeError. Fail soft to USD so one
      // bad stored code cannot take down a whole page.
      fmt = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits,
      });
    }
    _currencyFormatters.set(key, fmt);
  }
  return fmt;
};

/**
 * Resolve the currency code to display: the plant's code when set,
 * otherwise the tenant's default.
 */
export const resolveCurrencyCode = (plantCurrencyCode?: string | null): string => {
  const normalized = plantCurrencyCode?.trim();
  if (normalized) {
    return normalized;
  }
  return getTenantUnits().currencyCode;
};

export const formatCurrency = (
  amount: number | string | null | undefined,
  currencyCode?: Enums.CurrencyCode | null,
  locale?: string | null,
  maximumFractionDigits: number = 2,
): string => {
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }
  const numAmount = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(numAmount)) {
    return '';
  }
  // A blank code means the tenant default, so new call sites get it without resolving.
  const code = resolveCurrencyCode(currencyCode);
  const useLocale = locale ?? getTenantUnits().locale;
  return getCurrencyFormatter(code, useLocale, maximumFractionDigits).format(numAmount);
};

export const getCurrencySymbol = (currencyCode?: string | null): string => {
  const useLocale = getTenantUnits().locale;
  // A blank code means the tenant default, so new call sites get it without resolving.
  const formatted = getCurrencyFormatter(resolveCurrencyCode(currencyCode), useLocale).format(0);
  const symbol = formatted.replace(/[\s\d.,]/g, '');

  return symbol || '$';
};

// A flat amount applied across plants is added in each plant's own currency without conversion,
// so a symbol is only meaningful when every plant shares one currency; otherwise returns null.
export const sharedCurrencySymbol = (
  currencyCodes: Array<string | null | undefined>,
): string | null => {
  const distinct = new Set<string>(currencyCodes.filter(hasStringValue));
  return distinct.size === 1 ? getCurrencySymbol([...distinct][0]) : null;
};

export const clearCurrencyFormatterCache = (): void => {
  _currencyFormatters.clear();
};

export const formatDateTime = (value: DateTime | string | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return formatStringDateTime(value);
  }
  return formatLuxonDateTime(value);
};
